/**
 * 新建/编辑班级共用对话框（class-management:SC-01/03/04/37 前端）。
 *
 * 严格对照原型 prototype/admin/src/pages/classes/ClassEditModal.tsx：
 * 表单字段：班级名称、校区、课程 SKU、容量（InputNumber 默认 12）、教师
 * （按该校区教师岗位在职过滤）、默认教室（按校区过滤）、起止日期（RangePicker）；
 * 校区变更时清空教师/教室选择；编辑携带乐观锁 version。
 */
import { useEffect, useMemo, useState } from 'react';
import { DatePicker, Form, Input, InputNumber, Select } from 'antd';
import { message } from '../../utils/antd-message';
import { DraggableModal } from '../../components/DraggableModal';
import dayjs, { type Dayjs } from 'dayjs';
import {
  classesApi,
  type ClassListItem,
  type CreateClass,
} from '../../api/classes.api';
import { orgCampusApi, type CampusItem } from '../../api/campuses.api';
import { sortCampusesByNumericCode } from '../../utils/campus-sort';
import { courseSkuApi, type CourseSkuItem } from '../../api/course-skus.api';
import { staffApi, type StaffListItem } from '../../api/staff.api';
import { classroomsApi } from '../../api/classrooms.api';
import { errorMessage } from './ClassListPage';

interface ClassEditModalProps {
  editing: ClassListItem | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormValues {
  name: string;
  campusId: string;
  skuId: string;
  capacity: number;
  teacherId: string;
  classroomId: string;
  dateRange: [Dayjs, Dayjs];
}

export function ClassEditModal({ editing, onClose, onSaved }: ClassEditModalProps) {
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [campuses, setCampuses] = useState<CampusItem[]>([]);
  const [skus, setSkus] = useState<CourseSkuItem[]>([]);
  const [staffs, setStaffs] = useState<StaffListItem[]>([]);
  const [classrooms, setClassrooms] = useState<
    { id: string; name: string; campusId: string }[]
  >([]);

  const campusId = Form.useWatch('campusId', form);

  useEffect(() => {
    void (async () => {
      try {
        const [c, s, st, cr] = await Promise.all([
          orgCampusApi.listCampuses({ page: 1, pageSize: 100 }),
          courseSkuApi.listCourseSkus({ page: 1, pageSize: 100 }),
          // pageSize 上限对齐服务端 DTO 校验（@Max(100)），超限整包被拒导致下拉全空
          staffApi.listStaffs({ position: 'TEACHER' as never, page: 1, pageSize: 100 }),
          classroomsApi.listClassrooms({ page: 1, pageSize: 100 }),
        ]);
        setCampuses(sortCampusesByNumericCode(c.items));
        setSkus(s.items);
        setStaffs(st.items);
        setClassrooms(cr.items);
      } catch {
        // 选项加载失败由提交校验兜底
      }
    })();
  }, []);

  useEffect(() => {
    if (editing) {
      form.setFieldsValue({
        name: editing.name,
        campusId: editing.campusId,
        skuId: editing.skuId,
        capacity: editing.capacity,
        teacherId: editing.teacherId,
        classroomId: editing.defaultClassroomId,
        dateRange: [dayjs(editing.startDate), dayjs(editing.endDate)],
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ capacity: 12 });
    }
  }, [editing, form]);

  /** 教师选项：该校区教师岗位在职员工（原型过滤条件）。 */
  const teacherOptions = useMemo(
    () =>
      // 岗位在职教师（列表接口已按 position=TEACHER 筛选）；O-28：按表单校区
      // campusGrants 过滤（未选校区时选项空引导先选校区），后端
      // CreateClassHandler 校验仍兜底（错误信封提示）
      staffs
        .filter(
          (s) =>
            s.status === 'ACTIVE' &&
            !!campusId &&
            (s.campusGrants ?? []).some((g) => g.campusId === campusId),
        )
        .map((s) => ({ value: s.id, label: s.name })),
    [staffs, campusId],
  );

  /** 教室选项：同校区。 */
  const classroomOptions = useMemo(
    () =>
      classrooms
        .filter((r) => !campusId || r.campusId === campusId)
        .map((r) => ({ value: r.id, label: r.name })),
    [classrooms, campusId],
  );

  /** SKU 选项：非停用（编辑时含当前班级原 SKU）。 */
  const skuOptions = useMemo(
    () =>
      skus
        .filter(
          (s) => s.status !== 'DISABLED' || s.id === editing?.skuId,
        )
        .map((s) => ({ value: s.id, label: s.name })),
    [skus, editing],
  );

  const handleOk = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const payload: CreateClass = {
        campusId: values.campusId,
        skuId: values.skuId,
        name: values.name,
        capacity: values.capacity,
        teacherId: values.teacherId,
        defaultClassroomId: values.classroomId,
        startDate: values.dateRange[0].format('YYYY-MM-DD'),
        endDate: values.dateRange[1].format('YYYY-MM-DD'),
      };
      if (editing) {
        await classesApi.updateClass(editing.id, {
          name: payload.name,
          capacity: payload.capacity,
          teacherId: payload.teacherId,
          defaultClassroomId: payload.defaultClassroomId,
          expectedVersion: editing.version,
        });
        message.success('班级已更新');
      } else {
        await classesApi.createClass(payload);
        message.success('班级已创建');
      }
      onSaved();
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DraggableModal
      title={editing ? '编辑班级' : '新增班级'}
      open
      onOk={() => void handleOk()}
      onCancel={onClose}
      confirmLoading={submitting}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="班级名称"
          name="name"
          rules={[{ required: true, message: '请输入班级名称' }]}
        >
          <Input placeholder="如：初一数学A班" />
        </Form.Item>
        <Form.Item
          label="校区"
          name="campusId"
          rules={[{ required: true, message: '请选择校区' }]}
        >
          <Select
            style={{ width: '100%' }}
            disabled={!!editing}
            options={campuses.map((c) => ({ value: c.id, label: c.name }))}
            onChange={() =>
              form.setFieldsValue({ teacherId: undefined, classroomId: undefined })
            }
          />
        </Form.Item>
        <Form.Item
          label="课程SKU"
          name="skuId"
          rules={[{ required: true, message: '请选择课程SKU' }]}
        >
          <Select
            style={{ width: '100%' }}
            disabled={!!editing}
            options={skuOptions}
          />
        </Form.Item>
        <Form.Item
          label="容量"
          name="capacity"
          rules={[{ required: true, message: '请输入容量' }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="教师"
          name="teacherId"
          rules={[{ required: true, message: '请选择教师' }]}
        >
          <Select
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            options={teacherOptions}
          />
        </Form.Item>
        <Form.Item
          label="默认教室"
          name="classroomId"
          rules={[{ required: true, message: '请选择默认教室' }]}
        >
          <Select style={{ width: '100%' }} options={classroomOptions} />
        </Form.Item>
        <Form.Item
          label="起止日期"
          name="dateRange"
          rules={[{ required: true, message: '请选择起止日期' }]}
        >
          <DatePicker.RangePicker style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </DraggableModal>
  );
}
